import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpResponse
} from '@angular/common/http';

import {
  throwError as observableThrowError,
  Observable
} from 'rxjs';

import {
  catchError
} from 'rxjs/operators';

import {
  SupplierInvoiceModel,
  SupplierInvoiceSearchModel,
  SupplierModel,
  InvoiceTrackingModel,
  InvoiceTrackingEventModel
} from '../models/wks-suppliersInvoices.model';

import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SupplierInvoicesService {

  private httpHeaders: HttpHeaders;

  constructor(
    private httpClient: HttpClient
  ) {
  }

  /**
   * Définit les headers utilisés par le service.
   *
   * Les headers sont normalement alimentés
   * lors de l'identification de l'utilisateur.
   */
  setHeaders(
    _headers: HttpHeaders
  ): void {

    this.httpHeaders = _headers;

  }

  /**
   * Recherche des factures fournisseurs.
   *
   * POST :
   *
   * /wks/api/supplierinvoices/search
   */
  search(
    _entity: string,
    _criteria: SupplierInvoiceSearchModel
  ): Observable<HttpResponse<SupplierInvoiceModel[]>> {

    const urlCur =
      `${environment.baseUrl}/wks/api/supplierinvoices/search`;

    const body =
      JSON.stringify(_criteria);

    const localHttpHeaders =
      this.httpHeaders
        .set(
          'Content-Type',
          'application/json'
        )
        .set(
          'Accept',
          'application/json'
        )
        .set(
          'stdEntity',
          _entity
        );

    return this.httpClient
      .post<SupplierInvoiceModel[]>(
        urlCur,
        body,
        {
          headers: localHttpHeaders,
          observe: 'response'
        }
      )
      .pipe(
        catchError(
          this.handleErrorObservable
        )
      );
  }

  /**
   * Récupération des fournisseurs.
   *
   * POST :
   *
   * /wks/api/supplierinvoices/suppliers
   */
  getSuppliers(
  _entity: string
  ): Observable<HttpResponse<SupplierModel[]>> {

    const urlCur =
      `${environment.baseUrl}/wks/api/suppliers`;

    const localHttpHeaders =
      this.httpHeaders
        .set(
          'Content-Type',
          'application/json'
        )
        .set(
          'Accept',
          'application/json'
        )
        .set(
          'stdEntity',
          _entity
        );

    return this.httpClient
      .post<SupplierModel[]>(
        urlCur,
        null,
        {
          headers: localHttpHeaders,
          observe: 'response'
        }
      )
      .pipe(
        catchError(
          this.handleErrorObservable
        )
      );
  }

  /**
   * Récupération d'une facture fournisseur.
   *
   * GET :
   *
   * /wks/api/supplierinvoices/{id}
   */
  getInvoice(
    _id: string
  ): Observable<HttpResponse<SupplierInvoiceModel>> {

    const urlCur =
      `${environment.baseUrl}/wks/api/supplierinvoices/${_id}`;

    const localHttpHeaders =
      this.httpHeaders
        .set(
          'Accept',
          'application/json'
        );

    return this.httpClient
      .get<SupplierInvoiceModel>(
        urlCur,
        {
          headers: localHttpHeaders,
          observe: 'response'
        }
      )
      .pipe(
        catchError(
          this.handleErrorObservable
        )
      );
  }

/**
 * Récupération du PDF d'une facture fournisseur.
 *
 * GET :
 *
 * /wks/api/supplierinvoices/{id}/pdf
 *
 * La réponse est retournée sous forme de Blob.
 */
  getPdf(
    _ref: string,
    _entity: string,
  ): Observable<HttpResponse<Blob>> {

    const urlCur =
      `${environment.baseUrl}/wks/api/supplierinvoices/pdf`;

    const body = {
      ref: _ref
    };

    const localHttpHeaders =
      this.httpHeaders
        .set(
          'Accept',
          '*/*'
        )
        .set(
          'stdEntity',
          _entity
        );

    return this.httpClient
      .post(
        urlCur,
        body,
        {
          headers: localHttpHeaders,
          observe: 'response',
          responseType: 'blob'
        }
      )
      .pipe(
        catchError(
          this.handleErrorObservable
        )
      );
  }
  /**
   * Mise à jour du statut d'une facture fournisseur.
   *
   * PUT :
   *
   * /wks/api/supplierinvoices/{id}/status
   */
  updateStatus(
  _id: string,
  _status: string,
  _entity: string
  ): Observable<HttpResponse<any>> {

    const urlCur =
      `${environment.baseUrl}/wks/api/supplierinvoices/status`;

    const body = {
      id: _id,
      status: Number(_status),
    };

    const localHttpHeaders =
      this.httpHeaders
        .set(
          'Content-Type',
          'application/json'
        )
        .set(
          'Accept',
          'application/json'
        )
        .set(
          'stdEntity',
          _entity
        );

    return this.httpClient
      .post<any>(
        urlCur,
        body,
        {
          headers: localHttpHeaders,
          observe: 'response'
        }
      )
      .pipe(
        catchError(
          this.handleErrorObservable
        )
      );
  }

  /**
   * Récupération du tracking d'une facture.
   *
   * GET :
   *
   * /wks/api/supplierinvoices/tracking/{id}
   */
  getTracking(
    _entity: string,
    _invoiceId: number
  ): Observable<HttpResponse<InvoiceTrackingModel>> {

    const urlCur =
      `${environment.baseUrl}/wks/api/supplierinvoices/tracking`;

    const body =
      JSON.stringify({
        invoiceId: String(_invoiceId)
      });

    const localHttpHeaders =
      this.httpHeaders
        .set(
          'Content-Type',
          'application/json'
        )
        .set(
          'Accept',
          'application/json'
        )
        .set(
          'stdEntity',
          _entity
        );

    return this.httpClient
      .post<InvoiceTrackingModel>(
        urlCur,
        body,
        {
          headers: localHttpHeaders,
          observe: 'response'
        }
      )
      .pipe(
        catchError(
          this.handleErrorObservable
        )
      );
  }

  /**
   * Récupération des événements du tracking.
   *
   * GET :
   *
   * /wks/api/supplierinvoices/tracking/{trackingId}/events
   */
  getTrackingEvents(
    _entity: string,
    _invoiceId: string
  ): Observable<HttpResponse<InvoiceTrackingEventModel[]>> {

    const urlCur =
      `${environment.baseUrl}/wks/api/supplierinvoices/tracking-events`;

    const body = {
      invoiceId: _invoiceId
    };

    const localHttpHeaders =
      this.httpHeaders
        .set(
          'Content-Type',
          'application/json'
        )
        .set(
          'Accept',
          'application/json'
        )
        .set(
          'stdEntity',
          _entity
        );

    return this.httpClient
      .post<InvoiceTrackingEventModel[]>(
        urlCur,
        body,
        {
          headers: localHttpHeaders,
          observe: 'response'
        }
      )
      .pipe(
        catchError(
          this.handleErrorObservable
        )
      );
  }

  /**
   * Gestion des erreurs HTTP.
   */
  private handleErrorObservable(
    error: Response | any
  ) {

      return observableThrowError(
        error
      );


  }

  updateTrackingStatus(
    entity: string,
    invoiceId: number,
    trackingStatus: string,
    documentType: string,
    reasonCode: string,
    reason: string
  ): Observable<HttpResponse<any>> {

    const urlCur =
      `${environment.baseUrl}/wks/api/supplierinvoices/tracking-status`;

    const body = {
      invoiceId:
        String(invoiceId),

      trackingStatus:
        trackingStatus,

      documentType:
        documentType,

      reasonCode:
        reasonCode,

      reason:
        reason
    };

    const localHttpHeaders =
      this.httpHeaders
        .set(
          'Content-Type',
          'application/json'
        )
        .set(
          'Accept',
          'application/json'
        )
        .set(
          'stdEntity',
          entity
        );

    return this.httpClient
      .post<any>(
        urlCur,
        JSON.stringify(body),
        {
          headers: localHttpHeaders,
          observe: 'response'
        }
      )
      .pipe(
        catchError(
          this.handleErrorObservable
        )
      );
  }
/**
 * Impression de plusieurs factures fournisseurs.
 *
 * POST :
 *
 * /wks/api/supplierinvoices/print
 */
  printSuppiersInvoices(
    _invoiceIds: string[],
    _entity: string
  ): Observable<HttpResponse<Blob>> {

    const urlCur =
      `${environment.baseUrl}/wks/api/supplierinvoices/printSuppiersInvoices`;

    const body = {
      invoiceIds: _invoiceIds
    };

    const localHttpHeaders =
      this.httpHeaders
        .set(
          'Content-Type',
          'application/json'
        )
        .set(
          'Accept',
          'application/pdf'
        )
        .set(
          'stdEntity',
          _entity
        );

    return this.httpClient
      .post(
        urlCur,
        body,
        {
          headers: localHttpHeaders,
          observe: 'response',
          responseType: 'blob'
        }
      )
      .pipe(
        catchError(
          this.handleErrorObservable
        )
      );
  }
}

